/**
 * Microphone plumbing.
 *
 * Two independent jobs, deliberately kept apart:
 *
 *  1. **Onset detection** (Web Audio) — the exact millisecond the player
 *     started making noise. This is what the parry window is judged against,
 *     because speech recognition results arrive hundreds of milliseconds late
 *     and would make every parry look mistimed.
 *  2. **Recognition** (Web Speech API) — what they actually said. Runs in
 *     parallel and lands whenever it lands.
 *
 * Both degrade gracefully: no mic, or no recognition support, and the game
 * switches to typed chants without blocking the match.
 */

export interface MicLevels {
  /** Smoothed RMS, 0..1. Read every frame; never stored in React state. */
  level: number;
  /** Rolling peak used to draw the waveform. */
  peak: number;
  /** Epoch-aligned timestamp of the most recent speech onset, or 0. */
  lastOnsetAt: number;
  /** True while the analyser considers the player to be speaking. */
  speaking: boolean;
}

export const micLevels: MicLevels = { level: 0, peak: 0, lastOnsetAt: 0, speaking: false };

type OnsetHandler = (at: number) => void;

const ONSET_THRESHOLD = 0.055;
const RELEASE_THRESHOLD = 0.03;
/** Ignore a second onset inside this window — one utterance, one timestamp. */
const ONSET_DEBOUNCE_MS = 320;

class MicPipeline {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private analyser: AnalyserNode | null = null;
  private data: Float32Array<ArrayBuffer> | null = null;
  private raf = 0;
  private handlers = new Set<OnsetHandler>();
  private lastOnset = 0;

  granted = false;
  error: string | null = null;

  onOnset(fn: OnsetHandler): () => void {
    this.handlers.add(fn);
    return () => this.handlers.delete(fn);
  }

  /** The raw stream, so WebRTC can send the same microphone to the opponent. */
  get mediaStream(): MediaStream | null {
    return this.stream;
  }

  async start(): Promise<boolean> {
    if (this.granted) return true;
    if (!navigator.mediaDevices?.getUserMedia) {
      this.error = '이 브라우저는 마이크 입력을 지원하지 않습니다.';
      return false;
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      this.error =
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? '마이크 권한이 거부되었습니다. 키보드 영창으로 진행할 수 있습니다.'
          : '마이크를 열 수 없습니다. 키보드 영창으로 진행할 수 있습니다.';
      return false;
    }

    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctor();
    const source = this.ctx.createMediaStreamSource(this.stream);
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    this.analyser.smoothingTimeConstant = 0.05;
    source.connect(this.analyser);
    this.data = new Float32Array(new ArrayBuffer(this.analyser.fftSize * 4));
    this.granted = true;
    this.error = null;
    this.loop();
    return true;
  }

  private loop = () => {
    if (!this.analyser || !this.data) return;
    this.analyser.getFloatTimeDomainData(this.data);

    let sum = 0;
    for (let i = 0; i < this.data.length; i++) sum += this.data[i] * this.data[i];
    const rms = Math.sqrt(sum / this.data.length);

    // Fast attack, slow release: the attack half is what dates the onset.
    micLevels.level = rms > micLevels.level ? rms : micLevels.level * 0.82 + rms * 0.18;
    micLevels.peak = Math.max(micLevels.peak * 0.94, micLevels.level);

    const now = performance.timeOrigin + performance.now();
    if (!micLevels.speaking && micLevels.level > ONSET_THRESHOLD) {
      micLevels.speaking = true;
      if (now - this.lastOnset > ONSET_DEBOUNCE_MS) {
        this.lastOnset = now;
        micLevels.lastOnsetAt = now;
        for (const fn of this.handlers) fn(now);
      }
    } else if (micLevels.speaking && micLevels.level < RELEASE_THRESHOLD) {
      micLevels.speaking = false;
    }

    this.raf = requestAnimationFrame(this.loop);
  };

  /** Called when the tab hides: stop burning CPU on an analyser nobody sees. */
  suspend() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  resume() {
    if (this.granted && !this.raf) this.loop();
  }

  stop() {
    this.suspend();
    this.stream?.getTracks().forEach((t) => t.stop());
    void this.ctx?.close();
    this.ctx = null;
    this.stream = null;
    this.analyser = null;
    this.granted = false;
  }
}

export const mic = new MicPipeline();

/* ------------------------------------------------------------------ */
/* Speech recognition                                                  */
/* ------------------------------------------------------------------ */

interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: { isFinal: boolean; 0: { transcript: string; confidence: number } };
  };
}

type Ctor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): Ctor | null {
  const w = window as unknown as { SpeechRecognition?: Ctor; webkitSpeechRecognition?: Ctor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export const speechSupported = (): boolean => getRecognitionCtor() !== null;

export interface RecognitionUpdate {
  transcript: string;
  isFinal: boolean;
  at: number;
}

/**
 * A continuous English recogniser that restarts itself. Chrome ends a session
 * on every pause; without the restart the second round of a match would be
 * silent.
 */
export class ChantRecognizer {
  private recognition: SpeechRecognitionLike | null = null;
  private running = false;
  private handlers = new Set<(u: RecognitionUpdate) => void>();
  private restartTimer: number | null = null;
  private failureHandlers = new Set<(reason: string) => void>();

  lastError: string | null = null;
  /** True once recognition has failed in a way retrying will not fix. */
  failed = false;

  /**
   * Fires when recognition is unavailable for good — permission refused, no
   * speech service reachable, or the engine simply is not there (headless
   * Chromium defines the constructor but has no backend). The UI switches to
   * keyboard chants rather than leaving the player unable to cast.
   */
  onFailure(fn: (reason: string) => void): () => void {
    this.failureHandlers.add(fn);
    return () => this.failureHandlers.delete(fn);
  }

  private fail(reason: string) {
    if (this.failed) return;
    this.failed = true;
    this.running = false;
    for (const fn of this.failureHandlers) fn(reason);
  }

  onUpdate(fn: (u: RecognitionUpdate) => void): () => void {
    this.handlers.add(fn);
    return () => this.handlers.delete(fn);
  }

  start(): boolean {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return false;
    if (this.running) return true;

    const rec = new Ctor();
    rec.lang = 'en-US';
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      const at = performance.timeOrigin + performance.now();
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? '';
        if (!transcript) continue;
        for (const fn of this.handlers) fn({ transcript, isFinal: result.isFinal, at });
      }
    };
    rec.onerror = (e) => {
      this.lastError = e.error;
      // "no-speech" and "aborted" are routine and self-heal on restart.
      if (
        e.error === 'not-allowed' ||
        e.error === 'service-not-allowed' ||
        e.error === 'language-not-supported' ||
        e.error === 'network'
      ) {
        this.fail(e.error);
      }
    };
    rec.onend = () => {
      if (!this.running) return;
      this.restartTimer = window.setTimeout(() => {
        try {
          rec.start();
        } catch {
          /* already starting */
        }
      }, 180);
    };

    this.recognition = rec;
    this.running = true;
    try {
      rec.start();
    } catch (err) {
      this.fail(err instanceof Error ? err.message : 'start-failed');
      return false;
    }
    return true;
  }

  stop() {
    this.running = false;
    if (this.restartTimer !== null) window.clearTimeout(this.restartTimer);
    this.restartTimer = null;
    try {
      this.recognition?.abort();
    } catch {
      /* ignore */
    }
    this.recognition = null;
  }

  get isRunning() {
    return this.running;
  }
}

export const chantRecognizer = new ChantRecognizer();
